import { OAuthApi } from '@backstage/core-plugin-api';
import { createApiRef } from '@backstage/frontend-plugin-api';
import { GithubRepository } from './repository';

export const PAGE_SIZE = 20;
export type GithubResource = 'pull-requests' | 'workflows' | 'deployments';
export type GithubRow = {
  id: number;
  title: string;
  url: string;
  state: string;
  source?: string;
  sha?: string;
  actor?: string;
  updatedAt?: string;
};
export type GithubPage = {
  items: GithubRow[];
  hasNext: boolean;
  totalCount?: number;
};
export interface GithubApi {
  connect(): Promise<void>;
  list(
    resource: GithubResource,
    repository: GithubRepository,
    page: number,
    signal?: AbortSignal,
  ): Promise<GithubPage>;
}
export const githubApiRef = createApiRef<GithubApi>().with({
  id: 'plugin.github.api',
});

export class GithubAuthenticationRequired extends Error {
  constructor() {
    super('Connect your GitHub account to view this repository.');
    this.name = 'GithubAuthenticationRequired';
  }
}
export class GithubApiError extends Error {
  constructor(
    message: string,
    readonly ssoUrl?: string,
  ) {
    super(message);
    this.name = 'GithubApiError';
  }
}

type PullRequest = {
  id: number;
  number: number;
  title: string;
  state: string;
  draft?: boolean;
  merged_at?: string | null;
  head: { ref: string; sha: string };
  base: { ref: string };
  user?: { login: string } | null;
  updated_at: string;
};
type WorkflowRun = {
  id: number;
  name?: string;
  display_title?: string;
  run_number: number;
  status: string;
  conclusion?: string | null;
  head_branch?: string;
  head_sha?: string;
  actor?: { login: string } | null;
  updated_at: string;
};
type Environment = { id: number; name: string; updated_at: string };
type Deployment = {
  id: number;
  ref: string;
  sha: string;
  creator?: { login: string } | null;
  created_at: string;
};
type DeploymentStatus = { state: string; created_at: string };

function pullRequestState(pr: PullRequest): string {
  if (pr.merged_at) return 'merged';
  if (pr.draft && pr.state === 'open') return 'draft';
  return pr.state;
}

async function mapEnvironments<T, R>(
  items: T[],
  transform: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(4, items.length) }, async () => {
      while (next < items.length) {
        const index = next++;
        results[index] = await transform(items[index]);
      }
    }),
  );
  return results;
}

export class GithubClient implements GithubApi {
  constructor(
    private readonly auth: Pick<OAuthApi, 'getAccessToken'>,
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args),
  ) {}

  connect(): Promise<void> {
    // Keep the popup request synchronous with the Connect button's click.
    return this.auth
      .getAccessToken(['repo'], { instantPopup: true })
      .then(() => undefined)
      .catch(() => {
        throw new GithubApiError(
          'GitHub sign-in was not completed. Try again.',
        );
      });
  }

  private async request<T>(
    repository: GithubRepository,
    path: string,
    params: Record<string, string>,
    token: string,
    signal?: AbortSignal,
  ): Promise<{ data: T; hasNext: boolean }> {
    const url = new URL(
      `${repository.apiBaseUrl}/repos/${encodeURIComponent(repository.owner)}/${encodeURIComponent(repository.name)}/${path}`,
    );
    url.search = new URLSearchParams(params).toString();
    let response: Response;
    try {
      response = await this.fetchImpl(url.toString(), {
        headers: {
          Accept: 'application/vnd.github+json',
          Authorization: `Bearer ${token}`,
          'X-GitHub-Api-Version': '2022-11-28',
        },
        credentials: 'omit',
        signal,
      });
    } catch (error) {
      if (signal?.aborted) throw error;
      throw new GithubApiError('Unable to reach GitHub. Try again.');
    }
    if (!response.ok) {
      const sso = response.headers.get('X-GitHub-SSO');
      const candidate = sso?.match(/url=([^;\s,]+)/)?.[1];
      let ssoUrl: string | undefined;
      if (candidate) {
        try {
          const parsed = new URL(candidate);
          if (parsed.protocol === 'https:' && parsed.host === repository.host) {
            ssoUrl = parsed.toString();
          }
        } catch {
          // An invalid authorization URL must never become a link.
        }
      }
      if (sso?.includes('required')) {
        throw new GithubApiError(
          'Authorize your GitHub session for this organization through SSO, then retry.',
          ssoUrl,
        );
      }
      if (response.status === 401) {
        throw new GithubApiError(
          'Your GitHub session has expired. Reconnect GitHub in Settings and retry.',
        );
      }
      if (
        response.status === 429 ||
        response.headers.get('X-RateLimit-Remaining') === '0'
      ) {
        throw new GithubApiError(
          'GitHub API rate limit reached. Try again later.',
        );
      }
      if (response.status === 403) {
        throw new GithubApiError(
          'GitHub denied access. Check your repository access, organization SSO and GitHub App read permissions.',
        );
      }
      if (response.status === 404) {
        throw new GithubApiError(
          'GitHub could not find this resource. Check the repository annotation and your access.',
        );
      }
      throw new GithubApiError(
        `GitHub is unavailable (HTTP ${response.status}). Retry shortly.`,
      );
    }
    return {
      data: (await response.json()) as T,
      hasNext: /;\s*rel="next"/.test(response.headers.get('Link') ?? ''),
    };
  }

  async list(
    resource: GithubResource,
    repository: GithubRepository,
    page: number,
    signal?: AbortSignal,
  ): Promise<GithubPage> {
    if (!Number.isInteger(page) || page < 1)
      throw new Error('Invalid GitHub page.');
    const token = await this.auth.getAccessToken(['repo'], { optional: true });
    if (signal?.aborted)
      throw new DOMException('Request aborted', 'AbortError');
    if (!token) throw new GithubAuthenticationRequired();
    const pagination = { per_page: String(PAGE_SIZE), page: String(page) };
    if (resource === 'pull-requests') {
      const { data, hasNext } = await this.request<PullRequest[]>(
        repository,
        'pulls',
        { ...pagination, state: 'all', sort: 'updated', direction: 'desc' },
        token,
        signal,
      );
      return {
        hasNext,
        items: data.map(pr => ({
          id: pr.id,
          title: `#${pr.number} ${pr.title}`,
          url: `${repository.webUrl}/pull/${pr.number}`,
          state: pullRequestState(pr),
          source: `${pr.head.ref} → ${pr.base.ref}`,
          actor: pr.user?.login,
          updatedAt: pr.updated_at,
        })),
      };
    }
    if (resource === 'workflows') {
      const { data, hasNext } = await this.request<{
        total_count: number;
        workflow_runs: WorkflowRun[];
      }>(repository, 'actions/runs', pagination, token, signal);
      return {
        hasNext,
        totalCount: data.total_count,
        items: data.workflow_runs.map(run => ({
          id: run.id,
          title: `${run.display_title || run.name || 'Workflow'} #${run.run_number}`,
          url: `${repository.webUrl}/actions/runs/${run.id}`,
          state: run.conclusion ?? run.status,
          source: run.head_branch,
          sha: run.head_sha,
          actor: run.actor?.login,
          updatedAt: run.updated_at,
        })),
      };
    }

    const { data, hasNext } = await this.request<{
      total_count: number;
      environments: Environment[];
    }>(repository, 'environments', pagination, token, signal);
    const items = await mapEnvironments(
      data.environments,
      async environment => {
        const { data: deployments } = await this.request<Deployment[]>(
          repository,
          'deployments',
          { environment: environment.name, per_page: '1', page: '1' },
          token,
          signal,
        );
        const latest = deployments[0];
        let status: DeploymentStatus | undefined;
        if (latest) {
          const result = await this.request<DeploymentStatus[]>(
            repository,
            `deployments/${latest.id}/statuses`,
            { per_page: '1', page: '1' },
            token,
            signal,
          );
          status = result.data[0];
        }
        const filter = new URLSearchParams({
          environments_filter: environment.name,
        });
        return {
          id: environment.id,
          title: environment.name,
          url: `${repository.webUrl}/deployments/activity_log?${filter}`,
          state: latest ? (status?.state ?? 'pending') : 'no deployments',
          source: latest?.ref,
          sha: latest?.sha,
          actor: latest?.creator?.login,
          updatedAt:
            status?.created_at ?? latest?.created_at ?? environment.updated_at,
        };
      },
    );
    return { items, hasNext, totalCount: data.total_count };
  }
}
